import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Form, Row, Col, Spinner, Badge, Card } from "react-bootstrap";
import { FaPlus, FaTrash, FaEdit, FaTimes, FaSave, FaListUl } from "react-icons/fa";
import Swal from "sweetalert2";

import {
  getAdminLookups,
  saveAdminLookup,
  deleteAdminLookup,
} from "../../services/adminApi";

import ReusableAdminTable from "./ReusableAdminTable";

import "./AdminLookupPage.css";

const AdminLookupPage = () => {
  // =========================================
  // STATES
  // =========================================

  const [lookups, setLookups] = useState([]);

  const [loading, setLoading] = useState(true);

  const [view, setView] = useState("list");

  const [groupName, setGroupName] = useState("");

  const [items, setItems] = useState([
    {
      value: "",
      label: "",
    },
  ]);

  const [editingId, setEditingId] =
    useState(null);

  // =========================================
  // FETCH LOOKUPS
  // =========================================

  const fetchLookups = useCallback(async () => {
    setLoading(true);

    try {
      const res = await getAdminLookups();

      setLookups(res?.data || []);
    } catch (e) {
      console.error(e);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load lookups",
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // =========================================
  // INITIAL LOAD
  // =========================================

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  // =========================================
  // FORM METHODS
  // =========================================

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        value: "",
        label: "",
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    setItems(
      items.filter((_, i) => i !== index)
    );
  };

  const handleItemChange = (
    index,
    field,
    value
  ) => {
    const newItems = [...items];

    newItems[index][field] = value;

    setItems(newItems);
  };

  // =========================================
  // RESET FORM
  // =========================================

  const resetForm = () => {
    setGroupName("");

    setItems([
      {
        value: "",
        label: "",
      },
    ]);

    setEditingId(null);

    setView("list");
  };

  // =========================================
  // SAVE
  // =========================================

  const handleSave = async () => {
    if (!groupName) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Group Name is required",
        confirmButtonColor: "#dc2626",
      });

      return;
    }

    try {
      await saveAdminLookup({
        lookupGroup: groupName,
        dataList: items,
      });

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Lookup saved successfully",
        confirmButtonColor: "#2563eb",
      });

      resetForm();

      fetchLookups();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: "Unable to save lookup",
        confirmButtonColor: "#dc2626",
      });
    }
  };

  // =========================================
  // EDIT
  // =========================================

  const handleEdit = (lk) => {
    setGroupName(lk.lookupGroup);

    setItems(
      lk.lookupValue?.data?.map((d) => ({
        value: d.lookupValue,
        label: d.lookupLabel,
      })) || []
    );

    setEditingId(lk.id);

    setView("form");
  };

  // =========================================
  // DELETE
  // =========================================

  const handleDelete = async (
    lookupGroup
  ) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete ${lookupGroup}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteAdminLookup(lookupGroup);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Lookup deleted successfully",
        confirmButtonColor: "#2563eb",
      });

      fetchLookups();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: "Unable to delete lookup",
        confirmButtonColor: "#dc2626",
      });
    }
  };

  // =========================================
  // TABLE COLUMNS
  // =========================================

  const columns = useMemo(
    () => [
      {
        accessorKey: "lookupGroup",
        header: "Group",

        cell: (info) => (
          <div className="fw-semibold text-white">
            {info.getValue()}
          </div>
        ),
      },

      {
        id: "itemsCount",

        accessorKey: "lookupValue",

        header: "Items Count",

        cell: (info) => (
          <Badge
            bg="dark"
            className="border border-secondary px-3 py-2"
          >
            {info.row.original.lookupValue
              ?.total || 0}{" "}
            Items
          </Badge>
        ),
      },

      {
        id: "preview",

        accessorKey: "lookupValue",

        header: "Preview",

        cell: (info) => {
          const values =
            info.row.original.lookupValue
              ?.data || [];

          return (
            <div className="d-flex flex-wrap gap-1">
              {values
                .slice(0, 3)
                .map((item, index) => (
                  <Badge
                    key={index}
                    bg="secondary"
                  >
                    {item.lookupLabel}
                  </Badge>
                ))}

              {values.length > 3 && (
                <Badge bg="info">
                  +{values.length - 3}
                </Badge>
              )}
            </div>
          );
        },
      },

      {
        id: "actions",

        header: "Actions",

        cell: (info) => {
          const lk = info.row.original;

          return (
            <div className="d-flex justify-content-center gap-2">
              <Button
                size="sm"
                variant="primary"
                onClick={() =>
                  handleEdit(lk)
                }
              >
                <FaEdit />
              </Button>

              <Button
                size="sm"
                variant="danger"
                onClick={() =>
                  handleDelete(
                    lk.lookupGroup
                  )
                }
              >
                <FaTrash />
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  // =========================================
  // RENDER
  // =========================================

  return (
    <div className="admin-lookup-page">
      {/* ========================================= */}
      {/* LIST VIEW */}
      {/* ========================================= */}

      {view === "list" ? (
        <ReusableAdminTable
          data={lookups}
          columns={columns}
          isLoading={loading}
          title="Lookup Registry"
          onAddNew={() =>
            setView("form")
          }
          addNewButtonText="New Lookup"
          searchPlaceholder="Search lookups..."
          exportCsvFilename="lookup-registry.csv"
        />
      ) : (
        /* ========================================= */
        /* FORM VIEW */
        /* ========================================= */

        <Card className="lookup-card-main bg-dark border-secondary text-white">
          <Card.Body>
            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h3>
                  {editingId
                    ? "Edit Lookup Group"
                    : "Add Lookup Group"}
                </h3>

                <p className="text-muted small mb-0">
                  Define and manage
                  system-wide dropdown
                  constants.
                </p>
              </div>

              <Button
                variant="secondary"
                onClick={resetForm}
              >
                <FaTimes className="me-2" />
                Cancel
              </Button>
            </div>

            {/* GROUP NAME */}
            <Row className="mb-4">
              <Col md={6}>
                <Form.Label>
                  Group Identifier
                </Form.Label>

                <Form.Control
                  className="bg-dark text-white border-secondary"
                  value={groupName}
                  onChange={(e) =>
                    setGroupName(
                      e.target.value
                    )
                  }
                  placeholder="e.g. skinType"
                  disabled={!!editingId}
                />

                <small className="text-muted mt-1 d-block">
                  Used internally to
                  fetch these options.
                </small>
              </Col>
            </Row>

            {/* ROWS */}
            <h6 className="mt-5 mb-3">
              <FaListUl className="me-2" />
              Row Entries
            </h6>

            <div className="items-container mb-4">
              {items.map((item, index) => (
                <Row
                  key={index}
                  className="mb-3 g-2 align-items-center"
                >
                  {/* VALUE */}
                  <Col md={5}>
                    <Form.Control
                      className="bg-dark text-white border-secondary"
                      placeholder="Value"
                      value={item.value}
                      onChange={(e) =>
                        handleItemChange(
                          index,
                          "value",
                          e.target.value
                        )
                      }
                    />
                  </Col>

                  {/* LABEL */}
                  <Col md={5}>
                    <Form.Control
                      className="bg-dark text-white border-secondary"
                      placeholder="Label"
                      value={item.label}
                      onChange={(e) =>
                        handleItemChange(
                          index,
                          "label",
                          e.target.value
                        )
                      }
                    />
                  </Col>

                  {/* REMOVE */}
                  <Col md={2}>
                    {items.length >
                      1 && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() =>
                          handleRemoveItem(
                            index
                          )
                        }
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}

              {/* ADD ROW */}
              <Button
                variant="outline-info"
                size="sm"
                onClick={handleAddItem}
              >
                <FaPlus className="me-1" />
                Add Row
              </Button>
            </div>

            {/* FOOTER */}
            <div className="d-flex justify-content-between align-items-center border-top border-secondary pt-4 mt-5">
              <Button
                variant="secondary"
                onClick={resetForm}
              >
                DISCARD
              </Button>

              <Button
                variant="primary"
                onClick={handleSave}
              >
                <FaSave className="me-2" />
                SAVE
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default AdminLookupPage;